import MicroModal from 'micromodal'

import { getRecaptchaToken, RECAPTCHA_FIELD } from './recaptcha'
import { lockScroll, unlockScroll } from './scrollLock'

/** MicroModal id — must match include/registration-modal.php. */
const MODAL_ID = 'modal-registration'

const STATUS_CLASSES = {
  success: ['border-brand', 'bg-brand/10', 'text-night-950'],
  error: ['border-red-500/40', 'bg-red-500/5', 'text-red-700'],
}

/**
 * Registration modal (MicroModal) and its form.
 *
 * Triggers are `.js-registration-open` elements — the header's Registration
 * buttons. They stay real links to /registration so the form is still reachable
 * with JS off; here we take the click over and open the modal instead.
 *
 * The form posts to admin-ajax rather than navigating, so a failed submission
 * keeps everything the user typed and the modal never reloads out from under
 * them. Scroll is locked while open with the shared Lenis-aware helper, same as
 * the video modal and lightbox.
 *
 * @param {import('lenis').default|null} [lenis] - smooth-scroll instance to pause
 */
export function initRegistrationModal(lenis = null) {
  const modal = document.getElementById(MODAL_ID)
  if (!modal) return

  const form = modal.querySelector('.js-registration-form')
  // getAttribute, not `form.action`: admin-ajax needs a field named `action`,
  // and a named control shadows the same-named property on its form — so
  // `form.action` hands back that <input>, which fetch() would stringify into a
  // bogus relative URL.
  const endpoint = form?.getAttribute('action')
  const status = modal.querySelector('.js-registration-status')
  const submit = modal.querySelector('.js-registration-submit')
  const properties = Array.from(modal.querySelectorAll('.js-registration-property'))

  // MicroModal.show() takes its config per call (init()'s isn't reused), so the
  // options live here and are passed in at each open.
  const options = {
    awaitCloseAnimation: true,
    disableScroll: false, // we lock scroll ourselves (Lenis-aware, no jump)
    onShow: () => lockScroll(lenis),
    onClose: () => unlockScroll(lenis),
  }

  document.querySelectorAll('.js-registration-open').forEach((trigger) => {
    trigger.addEventListener('click', (event) => {
      event.preventDefault()
      MicroModal.show(MODAL_ID, options)
    })
  })

  if (!form) return

  const setStatus = (kind, message) => {
    if (!status) return
    status.classList.remove(...STATUS_CLASSES.success, ...STATUS_CLASSES.error)
    status.classList.add(...STATUS_CLASSES[kind])
    status.textContent = message
    status.hidden = false
  }

  const clearStatus = () => {
    if (status) status.hidden = true
  }

  // A checkbox group has no native "at least one" rule, so mirror the server's
  // check here. The message rides on the first box, which lets the browser
  // focus and report it exactly like a missing required field.
  const validateProperties = () => {
    if (!properties.length) return true
    const chosen = properties.some((box) => box.checked)
    properties[0].setCustomValidity(chosen ? '' : 'Please select at least one interested property.')
    return chosen
  }

  properties.forEach((box) => box.addEventListener('change', validateProperties))

  form.addEventListener('submit', async (event) => {
    event.preventDefault()

    // The form is `novalidate` so this runs after the group check above —
    // otherwise the browser would report the built-in fields first and the
    // group's message would never be set.
    validateProperties()
    if (!form.reportValidity()) return

    clearStatus()
    submit.disabled = true
    const label = submit.textContent
    submit.textContent = 'Sending…'

    try {
      const body = new FormData(form)
      // Fetched per submit: a token expires two minutes after it's issued.
      body.set(RECAPTCHA_FIELD, await getRecaptchaToken(form.dataset.recaptcha, 'registration'))

      const response = await fetch(endpoint, {
        method: 'POST',
        body,
        credentials: 'same-origin',
      })
      // WordPress signals failure in the body as well as the status, so the
      // payload is what we read. A non-JSON reply falls through to the generic
      // message below rather than being reported as a network failure.
      const result = await response.json().catch(() => null)

      if (result?.success) {
        setStatus('success', result.data?.message || 'Thank you for registering.')
        form.reset()
        validateProperties()
        return
      }

      setStatus('error', result?.data?.message || 'Something went wrong. Please try again.')
    } catch {
      // Network-level failure — the response never arrived, so we can't say
      // whether the submission landed.
      setStatus('error', 'We could not reach the server. Please check your connection and try again.')
    } finally {
      submit.disabled = false
      submit.textContent = label
    }
  })
}
